import type { ReactNode } from 'react'

type Props = {
  /** O texto do `<summary>` — o que se lê com a dobra fechada. */
  titulo: string
  children: ReactNode
}

/**
 * Uma seção que fica **fechada** por padrão e abre com um toque.
 *
 * ## Por que ela existe: a página de produto tinha sete blocos do mesmo peso
 *
 * Herói, comparação de lojas, botão de registrar, cartão de números,
 * histórico, apelidos e ficha — todos cartões de vidro empilhados, todos
 * pedindo atenção igual. A decisão 203 já tinha resolvido isso uma vez, e a
 * tela cresceu de novo desde então.
 *
 * A pergunta que se faz na gôndola é *onde eu compro isto*. O que responde
 * essa pergunta fica aberto; o que responde *como foi da última vez* e *o que
 * é este produto* desce para dobras. Nada some — muda quanto custa chegar.
 *
 * ## Linha, e não cartão
 *
 * Uma dobra fechada é uma linha com borda embaixo, como a lista de Ajustes.
 * Se fosse um cartão de vidro, fechada ela ainda pesaria o mesmo que as
 * seções abertas, e o problema voltaria inteiro. Isto também mantém o
 * orçamento de vidro (`Glass`: no máximo 6 `frost` por viewport) longe do
 * teto — três dobras fechadas custam zero.
 *
 * ## `flex` vence `.alvo`, e isso é de propósito
 *
 * `.alvo` mora em `@layer base` e declara `inline-flex`; as utilities do
 * Tailwind vencem base, então `flex w-full` manda no layout e o que sobra do
 * `.alvo` é só o piso de 44px de altura — que é justamente o que se quer dele.
 *
 * ## O `<section>` por fora não é embrulho à toa
 *
 * Ele dá um nome à região, e é o que permite pular de seção em seção com
 * leitor de tela sem abrir nenhuma delas. O `<details>` controla o que se vê;
 * o `<section>` controla o que se encontra.
 */
export function Dobra({ titulo, children }: Props) {
  return (
    <section aria-label={titulo}>
      <details className="group border-b border-[var(--border)]">
        <summary className="alvo flex w-full cursor-pointer list-none items-center justify-between gap-2 text-sm">
          {titulo}
          {/* A seta é a única pista de que isto abre. Sem ela o `<summary>` lê
              como um título solto — visto em produção, na ficha. */}
          <span
            aria-hidden="true"
            className="inline-block text-[var(--text-muted)] transition-transform group-open:rotate-90"
          >
            ›
          </span>
        </summary>
        <div className="pb-4">{children}</div>
      </details>
    </section>
  )
}
